import { upperFirst } from "lodash";
import * as React from "react";

import { ExternalLink } from "components/external-link";
import { isInIframe } from "helpers/app";
import { isMessageEmpty } from "helpers/text-editor";
import { getToastContent as getTicketsToastContent } from "helpers/tickets";
import { TicketSilo, TicketStatusFormattedValues } from "interfaces/entities/ticket";
import { BulkActionProgressBar } from "routes/tickets/bulk-actions/Dropdowns";
import { ISinglePayloadError } from "services/websocket/interfaces";
import { IEntityCreateFailure, IRequestErrorWithDetails } from "store/entities/actions";
import { IBatchCreateAgentsFailurePayload } from "store/entities/agents";
import { ICreateEmailDomainFailurePayload } from "store/entities/email-domains";
import { IImportLiveChatAgentFailurePayload } from "store/views/agents";
import { ITicketsViewBatchUpdatePayload, ITicketsViewSendMessagePayload } from "store/views/tickets/interfaces";
import { IInviteAgentsFailurePayload, IInviteAgentsSuccessPayload } from "store/views/welcome-tutorial";
import { FileUploadErrorType } from "utils/file";
import { KeyMap } from "utils/interface";
import { plural } from "utils/string";

export enum ToastContent {
  WEBPUSH_SUBSCRIPTION_SUCCESS,
  WEBPUSH_SUBSCRIPTION_FAILURE,
  UKRAINE_SUPPORT_FOOTER_SUCCESS,
  POWERED_BY_FOOTER_SUCCESS,
  POWERED_BY_FOOTER_FAILURE,
  BILLING_DETAILS_UPDATED,
  BILLING_DETAILS_UPDATE_FAILURE,
  SUBSCRIPTION_DELETED,
  SUBSCRIPTION_UPDATE_FAILURE,
  COMPANY_NAME_UPDATED,
  CREATE_TICKET_SUCCESS,
  CREATE_TICKET_ERROR,
  DELETE_TICKET_SUCCESS,
  DELETE_TICKET_ERROR,
  DOMAIN_CONFIGURATION_COPIED,
  FORWARDING_ADDRESS_COPIED,
  FETCH_NEXT_TICKET_FAILED,
  FETCH_PREV_TICKET_FAILED,
  TICKET_BATCH_UPDATE_SUCCESS,
  TICKET_FETCH_ORIGINAL_MESSAGE_FAILED,
  TICKET_FETCH_UNSTRIPPED_MESSAGE_FAILED,
  TICKET_BATCH_UPDATE_ERROR,
  TICKET_SUBJECT_UPDATE_SUCCESS,
  TICKET_SUBJECT_UPDATE_ERROR,
  TICKET_REQUESTER_UPDATE_SUCCESS,
  TICKET_REQUESTER_UPDATE_ERROR,
  TICKET_CCS_UPDATE_SUCCESS,
  TICKET_CCS_UPDATE_ERROR,
  TICKET_ASSIGNEE_UPDATE_SUCCESS,
  TICKET_ASSIGNEE_UPDATE_ERROR,
  TICKET_TEAMS_UPDATE_SUCCESS,
  TICKET_TEAMS_UPDATE_ERROR,
  TICKET_SEND_MESSAGE_SUCCESS,
  TICKET_SEND_MESSAGE_ERROR,
  TICKET_UNSPAM_SUCCESS,
  TICKET_UNSPAM_ERROR,
  TICKET_COPY_URL_SUCCESS,
  TICKET_COPY_REQUESTER_EMAIL_SUCCESS,
  TICKET_SUMMARIZE_SUCCESS,
  TICKET_SUMMARIZE_ERROR,
  APPLY_MACRO_SUCCESS,
  APPLY_MACRO_ERROR,
  REPORT_CREATE_ERROR,
  REPORT_CREATE_SUCCESS,
  REPORT_DELETE_ERROR,
  REPORT_DELETE_SUCCESS,
  REPORT_EXISTS_ERROR,
  REPORT_SAVE_ERROR,
  REPORT_SAVE_SUCCESS,
  REPORT_DATE_RANGE_TOO_LONG,
  AGENTS_ADD_FAILURE,
  AGENTS_IMPORT_LIVECHAT_AGENT_SUCCESS,
  AGENTS_IMPORT_LIVECHAT_AGENT_FAILURE,
  IMPORT_LIVECHAT_DATA_FETCH_FAILURE,
  IMPORT_LIVECHAT_DATA_START_SUCCESS,
  IMPORT_LIVECHAT_DATA_START_FAILURE,
  IMPORT_LIVECHAT_DATA_FINISHED_SUCCESS,
  IMPORT_LIVECHAT_DATA_FINISHED_FAILURE,
  WELCOME_TUTORIAL_AGENTS_INVITE_SUCCESS,
  WELCOME_TUTORIAL_AGENTS_INVITE_FAILURE,
  AGENT_UPDATE_SUCCESS,
  AGENT_UPDATE_FAILURE,
  AGENT_DELETE_SUCCESS,
  AGENT_DELETE_FAILURE,
  TEAM_CREATE_SUCCESS,
  TEAM_CREATE_FAILURE,
  TEAM_UPDATE_SUCCESS,
  TEAM_DELETE_SUCCESS,
  TEAM_DELETE_FAILURE,
  TEAM_NOTIFICATIONS_SUCCESS,
  TEAM_NOTIFICATIONS_FAILURE,
  COPY_MAILBOX_SUCCESS,
  EMAIL_DOMAIN_CREATE_SUCCESS,
  EMAIL_DOMAIN_CREATE_FAILURE,
  EMAIL_DOMAIN_DELETE_SUCCESS,
  TAG_ADD_SUCCESS,
  TAG_ADD_FAILURE,
  TAG_UPDATE_SUCCESS,
  TAG_UPDATE_FAILURE,
  TAG_DELETE_SUCCESS,
  TAG_DELETE_FAILURE,
  CANNED_RESPONSE_ADD_SUCCESS,
  CANNED_RESPONSE_ADD_FAILURE,
  CANNED_RESPONSE_UPDATE_SUCCESS,
  CANNED_RESPONSE_UPDATE_FAILURE,
  CANNED_RESPONSE_DELETE_SUCCESS,
  CANNED_RESPONSE_DELETE_FAILURE,
  MACRO_CREATE_SUCCESS,
  MACRO_CREATE_FAILURE,
  MACRO_UPDATE_SUCCESS,
  MACRO_UPDATE_FAILURE,
  MACRO_DELETE_SUCCESS,
  MACRO_DELETE_FAILURE,
  MAILBOX_CREATE_SUCCESS,
  MAILBOX_CREATE_FAILURE,
  MAILBOX_UPDATE_SUCCESS,
  MAILBOX_UPDATE_FAILURE,
  MAILBOX_DELETE_SUCCESS,
  MAILBOX_DELETE_FAILURE,
  FORWARDING_INSTRUCTIONS_SEND_SUCCESS,
  FORWARDING_INSTRUCTIONS_SEND_FAILURE,
  DEFAULT_ERROR,
  CUSTOM_VIEW_CREATE_SUCCESS,
  CUSTOM_VIEW_CREATE_FAILURE,
  CUSTOM_VIEW_UPDATE_SUCCESS,
  CUSTOM_VIEW_UPDATE_FAILURE,
  CUSTOM_VIEW_DELETE_SUCCESS,
  CUSTOM_VIEW_DELETE_FAILURE,
  CUSTOM_FIELD_CREATE_SUCCESS,
  CUSTOM_FIELD_CREATE_FAILURE,
  CUSTOM_FIELD_ACTIVATED_SUCCESS,
  CUSTOM_FIELD_DEACTIVATED_SUCCESS,
  CUSTOM_FIELD_UPDATE_SUCCESS,
  CUSTOM_FIELD_UPDATE_FAILURE,
  CUSTOM_FIELD_DELETE_SUCCESS,
  CUSTOM_FIELD_DELETE_FAILURE,
  TICKET_SILO_UPDATE_SUCCESS,
  TICKET_SILO_UPDATE_FAILURE,
  TRANSACTION_ATTACHMENTS_LIMIT_REACHED,
  TRANSACTION_ATTACHMENT_NOT_IN_VALID_FORMAT,
  TRANSACTION_NOT_FOUND,
  UPDATE_BILLING_ADDRESS_FAILED,
  UPDATE_BILLING_ADDRESS_SUCCESS,
  RULE_CREATE_SUCCESS,
  RULE_UPDATE_SUCCESS,
  RULE_ACTIVE_SUCCESS,
  RULE_INACTIVE_SUCCESS,
  RULE_UPDATE_FAILURE,
  RULE_CREATE_FAILURE,
  RULE_DELETE_SUCCESS,
  RULE_DELETE_FAILURE,
  EMAIL_TEMPLATE_CREATE_SUCCESS,
  EMAIL_TEMPLATE_CREATE_FAILURE,
  EMAIL_TEMPLATE_UPDATE_SUCCESS,
  EMAIL_TEMPLATE_UPDATE_FAILURE,
  EMAIL_TEMPLATE_DELETE_SUCCESS,
  EMAIL_TEMPLATE_DELETE_FAILURE,
  EMAIL_TEMPLATE_TEST_SUCCESS,
  EMAIL_TEMPLATE_TEST_FAILURE,
  GENERATE_REPORT_SUCCESS,
  GENERATE_REPORT_FAILURE,
  TICKET_PRIORITY_UPDATE_SUCCESS,
  TICKET_PRIORITY_UPDATE_ERROR,
  TICKET_STATUS_UPDATE_SUCCESS,
  TICKET_STATUS_UPDATE_ERROR,
  SCREEN_RECORDING_UPLOAD_IN_PROGRESS,
  SCREEN_RECORDING_FAILURE,
  SCREEN_RECORDING_UPLOAD_SUCCESS,
  TAG_REMINDER_SUCCESS,
  TAG_REMINDER_FAILURE,
  REPLY_ADDRESS_UPDATE_SUCCESS,
  REPLY_ADDRESS_UPDATE_FAILURE,
  REPLY_ADDRESS_CREATE_SUCCESS,
  REPLY_ADDRESS_CREATE_FAILURE,
  REPLY_ADDRESS_DELETE_SUCCESS,
  REPLY_ADDRESS_DELETE_FAILURE,
  CONTACT_FORM_CODE_COPIED,
  ADD_FOLLOWER_SUCCESS,
  ADD_FOLLOWER_FAILURE,
  DELETE_FOLLOWER_SUCCESS,
  DELETE_FOLLOWER_FAILURE,
  BULK_ACTION_PROGRESS,
  BULK_ACTION_FAILURE,
  BULK_ACTION_SUCCESS,
  BULK_DELETE_PROGRESS,
  BULK_DELETE_FAILURE,
  BULK_DELETE_SUCCESS,
  RESEND_VERIFICATION_SUCCESS,
  RESEND_INVITATION_SUCCESS,
  RESEND_INVITATION_FAILURE,
  REPORT_TOO_MUCH_TEAMS,
  REPORT_TOO_MUCH_AGENTS,
  REPORT_TOO_MUCH_TAGS,
  REPORT_TOO_MUCH_TAGS_IN_TICKET,
  REMOVE_BLOCKED_ENTRY_FAILURE,
  REMOVE_TRUSTED_ENTRY_FAILURE,
  REMOVE_BLOCKED_ENTRY_SUCCESS,
  REMOVE_TRUSTED_ENTRY_SUCCESS,
  ADD_TRUSTED_ENTRY_SUCCESS,
  ADD_BLOCKED_ENTRY_SUCCESS,
  ADD_BLOCKED_ENTRY_FAILURE,
  ADD_TRUSTED_ENTRY_FAILURE,
  QUEUED_TRUSTED_ENTRIES_PROGRESS,
  QUEUED_BLOCKED_ENTRIES_PROGRESS,
  QUEUED_TRUSTED_ENTRIES_PROGRESS_DONE,
  QUEUED_BLOCKED_ENTRIES_PROGRESS_DONE,
  DELETE_CONTACT_FORM_SUCCESS,
  DELETE_CONTACT_FORM_FAILURE,
  CREATE_CONTACT_FORM_SUCCESS,
  CREATE_CONTACT_FORM_FAILURE,
  UPDATE_CONTACT_FORM_SUCCESS,
  UPDATE_CONTACT_FORM_FAILURE,
  MERGE_TICKETS_PROGRESS,
  MERGE_TICKETS_SUCCESS,
  MERGE_TICKETS_FAILURE,
  UPDATE_DEFAULT_TEMPLATE_SUCCESS,
  UPDATE_DEFAULT_TEMPLATE_FAILURE,
  AI_TEXT_ADUST_ENHANCEMENT_SUCCESS,
  AI_TEXT_ADUST_ENHANCEMENT_FAILURE,
  AI_TEXT_EXPAND_ENHANCEMENT_SUCCESS,
  AI_TEXT_EXPAND_ENHANCEMENT_FAILURE,
  AI_TEXT_CORRECT_ENHANCEMENT_SUCCESS,
  AI_TEXT_CORRECT_ENHANCEMENT_FAILURE,
  APPLICATION_INSTALL_ERROR,
  APPLICATION_UNINSTALL_SUCCESS,
  APPLICATION_UNINSTALL_ERROR,
  ATTACHMENT_DELETE_FAILURE,
  ATTACHMENT_DELETE_SUCCESS,
  LOGO_UPLOAD_SUCCESS,
  LOGO_UPLOAD_FAILURE,
  OPENWIDGET_CONNECT_SUCCESS,
  OPENWIDGET_CONNECT_FAILURE,
  OPENWIDGET_DISCONNECT_SUCCESS,
  OPENWIDGET_DISCONNECT_FAILURE,
  OPENWIDGET_UPDATE_SUCCESS,
  OPENWIDGET_UPDATE_FAILURE,
  OPENWIDGET_COPY_CODE_SUCCESS,
  OPENWIDGET_COPY_CODE_FAILURE,
  AGENTS_LIMIT_IN_FILTERS_REACHED,
  LANGUAGES_LIMIT_IN_FILTERS_REACHED,
  AUTO_ASSIGNMENT_CHANGED_SUCCESS,
  AUTO_ASSIGNMENT_CHANGED_FAILURE,
}

type IToastContentMap = {
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key in ToastContent]: (payload?: KeyMap<any>) => React.ReactNode;
};

export interface IAssigneeUpdateToastPayload {
  assignee?: {
    name: string;
  };
}

export const contentMap: IToastContentMap = {
  [ToastContent.BILLING_DETAILS_UPDATED]: () => "Billing details have been updated.",
  [ToastContent.BILLING_DETAILS_UPDATE_FAILURE]: () =>
    "Billing details have not been updated. Please contact us to get help.",
  [ToastContent.SUBSCRIPTION_DELETED]: () => "Your subscription has been canceled.",
  // @ts-expect-error TS(2339): Property 'errorObject' does not exist on type 'KeyMap<any... Remove this comment to see the full error message
  [ToastContent.SUBSCRIPTION_UPDATE_FAILURE]: ({ type, error }) => {
    if (type === "conflict") {
      if (error.includes("You cannot reduce quantity below the minimum paid agents limit")) {
        return "You can't reduce the number of agents on your plan. Contact your Account Manager for more information.";
      }
    }
    return "Subscription has not been updated.";
  },
  [ToastContent.COMPANY_NAME_UPDATED]: () => "Your company name has been updated.",
  [ToastContent.UKRAINE_SUPPORT_FOOTER_SUCCESS]: () => "Footer settings changed successfully.",
  [ToastContent.POWERED_BY_FOOTER_FAILURE]: () => "Footer settings change failure.",
  [ToastContent.POWERED_BY_FOOTER_SUCCESS]: () => "Footer settings changed successfully.",
  [ToastContent.WEBPUSH_SUBSCRIPTION_SUCCESS]: () => "Notifications enabled.",
  // @ts-expect-error TS(2339): Property 'type' does not exist on type 'KeyMap<any... Remove this comment to see the full error message
  [ToastContent.WEBPUSH_SUBSCRIPTION_FAILURE]: ({ type }) => {
    if (type === "DUPLICATION") {
      return "You already have notifications enabled for this browser.";
    }
    return "Browser notifications was not set. Please try again.";
  },
  [ToastContent.CREATE_TICKET_SUCCESS]: () => "Ticket has been created.",
  // @ts-expect-error TS(2339): Property 'type' does not exist on type 'KeyMap<any... Remove this comment to see the full error message
  [ToastContent.CREATE_TICKET_ERROR]: ({ type, error, details }) => {
    if (type === "limitExceeded") {
      if (error.includes("hard bounces") || error.includes("spam notifications")) {
        return "Temporary lock. Too many email hard-bounces generated in the last 24h.";
      }
      if (error.includes("spam complaints")) {
        return "Temporary lock. Too many spam complaints generated in the last 24h.";
      }
      if (error.includes("verification")) {
        return "Message limit for unverified license exceeded. Please verify the account.";
      }
      if (error.includes("count limit")) {
        return "Message count limit for ticket exceeded.";
      }
      if (error.includes("tags count per ticket limit")) {
        return "Maximum number of tags in ticket is reached.";
      }
      if (error.includes("unsubscriptions")) {
        return "Temporary lock. Too many opt-outs generated in the last 24h.";
      }
      return "Message limit for trial license exceeded. Limit will be lifted automatically.";
    }
    if (type === "validation") {
      if (details?.errors[0].field === "requester.email") {
        return "Requester email is incorrect.";
      }
    }

    return "Ticket has not been created.";
  },
  [ToastContent.DELETE_TICKET_SUCCESS]: () => "Ticket has been deleted.",
  [ToastContent.DELETE_TICKET_ERROR]: () => "Ticket has not been deleted.",
  // @ts-expect-error TS(2418): Type of computed property's value is '(payload: IT... Remove this comment to see the full error message
  [ToastContent.TICKET_BATCH_UPDATE_SUCCESS]: (payload: ITicketsViewBatchUpdatePayload) =>
    getTicketsToastContent(payload.ticketIds, payload.batchAction, payload.data),
  [ToastContent.TICKET_BATCH_UPDATE_ERROR]: () => "Something went wrong. Tickets have not been updated.",
  [ToastContent.TICKET_SUBJECT_UPDATE_SUCCESS]: () => "Ticket subject has been updated.",
  [ToastContent.TICKET_SUBJECT_UPDATE_ERROR]: () => "Ticket subject has not been updated.",
  [ToastContent.TICKET_REQUESTER_UPDATE_SUCCESS]: () => "Ticket requester details have been updated.",
  [ToastContent.TICKET_REQUESTER_UPDATE_ERROR]: props => {
    const { error } = props as { error: IRequestErrorWithDetails };

    if (error.type.includes("validation") && error.details.errors[0].field === "requester.email") {
      return "Requester email is incorrect.";
    }

    return "Ticket requester details have not been updated.";
  },
  [ToastContent.TICKET_CCS_UPDATE_SUCCESS]: () => "People in the loop have been updated.",
  [ToastContent.TICKET_CCS_UPDATE_ERROR]: () => "People in the loop have not been updated.",
  // @ts-expect-error TS(2418): Type of computed property's value is '(payload: IA... Remove this comment to see the full error message
  [ToastContent.TICKET_ASSIGNEE_UPDATE_SUCCESS]: (payload: IAssigneeUpdateToastPayload) => {
    if (payload.assignee) {
      return `Assignee changed to ${payload.assignee.name}.`;
    }
    return "Assignee has been removed.";
  },
  [ToastContent.TICKET_ASSIGNEE_UPDATE_ERROR]: () => "Assignee has not been updated.",
  [ToastContent.TICKET_TEAMS_UPDATE_SUCCESS]: () => "Teams have been updated.",
  [ToastContent.TICKET_TEAMS_UPDATE_ERROR]: () => "Teams have not been updated.",
  [ToastContent.TICKET_PRIORITY_UPDATE_SUCCESS]: () => "Ticket priority has been updated.",
  [ToastContent.TICKET_PRIORITY_UPDATE_ERROR]: () => "Ticket priority has not been updated.",
  // @ts-expect-error TS(2418): Type of computed property's value is '(payload: { ... Remove this comment to see the full error message
  [ToastContent.TICKET_SILO_UPDATE_SUCCESS]: (payload: { silo: TicketSilo }) => {
    const { silo } = payload;
    switch (silo) {
      case TicketSilo.Archive:
        return "Ticket has been moved to Archive.";
      case TicketSilo.Spam:
        return "Ticket has been moved to Spam.";
      case TicketSilo.Trash:
        return "Ticket has been moved to Trash.";
      case TicketSilo.Tickets:
        return "Ticket has been moved to all recent tickets.";
      default:
        return "Ticket has been moved.";
    }
  },
  // @ts-expect-error TS(2418): Type of computed property's value is '(payload: { ... Remove this comment to see the full error message
  [ToastContent.TICKET_SILO_UPDATE_FAILURE]: (payload: { silo: TicketSilo }) => {
    const { silo } = payload;
    switch (silo) {
      case TicketSilo.Archive:
        return "Ticket has not been moved to Archive.";
      case TicketSilo.Spam:
        return "Ticket has not been moved to Spam.";
      case TicketSilo.Trash:
        return "Ticket has not been moved to Trash.";
      case TicketSilo.Tickets:
        return "Ticket has not been moved to all recent tickets.";
      default:
        return "Ticket has not been moved.";
    }
  },
  [ToastContent.TICKET_STATUS_UPDATE_SUCCESS]: () => "Ticket status has been updated.",
  [ToastContent.TICKET_STATUS_UPDATE_ERROR]: () => "Ticket status has not been updated.",
  // @ts-expect-error TS(2418): Type of computed property's value is '(payload: IT... Remove this comment to see the full error message
  [ToastContent.TICKET_SEND_MESSAGE_SUCCESS]: (payload: ITicketsViewSendMessagePayload) => {
    const { attachments, transactionID, message, status, isPrivate } = payload;
    const onlyStatusChanged =
      ((attachments && attachments.length === 0) || (!attachments && !transactionID)) && isMessageEmpty(message);
    if (onlyStatusChanged) {
      // @ts-expect-error TS(2538): Type 'undefined' cannot be used as an index type.
      return `Ticket status has been changed to ${TicketStatusFormattedValues[status]}.`;
    }

    if (isPrivate) {
      return "Private note has been sent.";
    }

    return "Message has been sent.";
  },
  // @ts-expect-error TS(2339): Property 'reason' does not exist on type 'KeyMap<a... Remove this comment to see the full error message
  [ToastContent.TICKET_SEND_MESSAGE_ERROR]: ({ reason, error }) => {
    if (reason === "limitExceeded") {
      if (error.includes("hard bounces") || error.includes("spam notifications")) {
        return "Temporary lock. Too many email hard-bounces generated in the last 24h.";
      }
      if (error.includes("spam complaints")) {
        return "Temporary lock. Too many spam complaints generated in the last 24h.";
      }
      if (error.includes("verification")) {
        return "Message limit for unverified license exceeded. Please verify the account.";
      }
      if (error.includes("count limit")) {
        return "Message count limit for ticket exceeded.";
      }
      if (error.includes("tags count per ticket limit")) {
        return "Maximum number of tags in ticket is reached.";
      }
      if (error.includes("unsubscriptions")) {
        return "Temporary lock. Too many opt-outs generated in the last 24h.";
      }
      return "Message limit for trial license exceeded. Limit will be lifted automatically.";
    }

    return `Message has not been sent.${reason ? ` ${reason}` : ""}`;
  },
  [ToastContent.TICKET_UNSPAM_SUCCESS]: () => "Ticket is no longer marked as spam.",
  [ToastContent.TICKET_UNSPAM_ERROR]: () => "Ticket status has not been changed.",
  [ToastContent.TICKET_COPY_URL_SUCCESS]: () => "Ticket URL has been copied.",
  [ToastContent.TICKET_COPY_REQUESTER_EMAIL_SUCCESS]: () => "Email address has been copied.",
  [ToastContent.TICKET_SUMMARIZE_SUCCESS]: () => "Summary has been created.",
  [ToastContent.TICKET_SUMMARIZE_ERROR]: () => "Summary has not been created.",
  [ToastContent.APPLY_MACRO_SUCCESS]: () => "Macro has been applied.",
  [ToastContent.APPLY_MACRO_ERROR]: () => "Macro has not been applied.",
  [ToastContent.DEFAULT_ERROR]: () => "Something went wrong. Please try again in few minutes.",
  // @ts-expect-error TS2418
  [ToastContent.AGENTS_ADD_FAILURE]: (payload: IBatchCreateAgentsFailurePayload) => {
    if (payload.errorObject?.type === "permissions") {
      return "You’ve reached the limit of added agents. Contact an admin to manage your subscription and add a new agent.";
    }
    if (payload.errorObject?.type === "conflict") {
      if (payload.errorObject.message.includes("too many organizations")) {
        return "This agent is in too many organizations.";
      }
      return "This agent already exists in HelpDesk.";
    }
    if (payload.errorObject?.error?.type === "limitExceeded") {
      if (payload.errorObject.error.message.includes("number of emails sent exceeded before verification")) {
        return "You can’t add an agent. This feature has been temporarily disabled. To unblock it, verify the account.";
      }
      if (payload.errorObject.error.message.includes("number of emails sent exceeded")) {
        return "You can’t add an agent. This feature has been temporarily disabled. Try again later.";
      }
      if (payload.errorObject.error.details?.lackingSeats) {
        const { lackingSeats } = payload.errorObject.error.details;
        return `Agent count limit has been reached. ${lackingSeats} ${plural(lackingSeats, "agent", "agents")} exceed your subscription limit. Contact support to increase the limit.`;
      }
      return "Agent count limit has been reached. Contact support to change the limit.";
    }
    if (payload.errorObject?.error?.type === "paymentIssue") {
      return "Subscription has not been updated. We had issues processing your payment.";
    }
    return contentMap[ToastContent.DEFAULT_ERROR]();
  },
  [ToastContent.AGENTS_IMPORT_LIVECHAT_AGENT_SUCCESS]: () => "Agent has been added to HelpDesk.",
  // @ts-expect-error TS2418
  [ToastContent.AGENTS_IMPORT_LIVECHAT_AGENT_FAILURE]: (payload: IImportLiveChatAgentFailurePayload) => {
    if (payload.errorObject?.type === "conflict") {
      if (payload.errorObject?.message.includes("too many organizations")) {
        return "This agent is in too many organizations.";
      }
      return "This agent already exists in HelpDesk.";
    }
    if (payload.errorObject?.type === "missingResource") {
      return "Agent not added. This account has been deleted";
    }
    if (payload.errorObject?.type === "limitExceeded") {
      if (payload.errorObject.details?.lackingSeats) {
        const { lackingSeats } = payload.errorObject.details;
        return `Agent count limit has been reached. ${lackingSeats} ${plural(lackingSeats, "agent", "agents")} exceed your subscription limit. Contact support to increase the limit.`;
      }
      return "Agent count limit has been reached. Contact support to change the limit.";
    }
    if (payload.errorObject?.type === "paymentIssue") {
      return "Subscription has not been updated. We had issues processing your payment. Check your payment details or contact us.";
    }
    return "Could not add this agent to HelpDesk.";
  },
  [ToastContent.IMPORT_LIVECHAT_DATA_FETCH_FAILURE]: () => "Could not fetch data from LiveChat.",
  [ToastContent.IMPORT_LIVECHAT_DATA_START_SUCCESS]: () => "LiveChat data import has been started.",
  [ToastContent.IMPORT_LIVECHAT_DATA_START_FAILURE]: () => "Could not import data from LiveChat.",
  [ToastContent.IMPORT_LIVECHAT_DATA_FINISHED_SUCCESS]: () => "LiveChat data import has been finished.",
  // @ts-expect-error TS(2418): Type of computed property's value is '(payload: { ... Remove this comment to see the full error message
  [ToastContent.IMPORT_LIVECHAT_DATA_FINISHED_FAILURE]: (payload: { error?: ISinglePayloadError }) => {
    if (payload.error?.errorCode === "planLimitExceeded") {
      return "Team import failed. Too many LiveChat groups to match with HelpDesk teams.";
    }
    if (payload.error?.errorCode === "limitExceeded") {
      return payload.error.details?.lackingSeats
        ? `LiveChat data import failed. ${payload.error.details.lackingSeats} ${plural(payload.error.details.lackingSeats, "user", "users")} exceed your subscription limit. Contact us for details.`
        : "LiveChat data import failed. Subscription has not been updated. Contact us for details.";
    }
    if (payload.error?.errorCode === "paymentIssue") {
      return "Subscription has not been updated. We had issues processing your payment. Check your payment details or contact us.";
    }
    return "LiveChat data import failed.";
  },
  // @ts-expect-error TS2418
  [ToastContent.WELCOME_TUTORIAL_AGENTS_INVITE_SUCCESS]: (payload: IInviteAgentsSuccessPayload) => {
    return `${plural(payload.agents.length, "Agent was", "Agents were")} invited successfully.`;
  },
  // @ts-expect-error TS2418
  [ToastContent.WELCOME_TUTORIAL_AGENTS_INVITE_FAILURE]: (payload: IInviteAgentsFailurePayload) => {
    if (payload.errorObject?.type === "conflict") {
      return "This agent already exists in HelpDesk.";
    }
    if (payload.errorObject?.error?.type === "limitExceeded") {
      if (payload.errorObject.error.message.includes("number of emails sent exceeded before verification")) {
        return "You can’t add an agent. This feature has been temporarily disabled. To unblock it, verify the account.";
      }
      if (payload.errorObject.error.message.includes("number of emails sent exceeded")) {
        return "You can’t add an agent. This feature has been temporarily disabled. Try again later.";
      }
      if (payload.errorObject.error.details?.lackingSeats) {
        const { lackingSeats } = payload.errorObject.error.details;
        return `Agent count limit has been reached. ${lackingSeats} ${plural(lackingSeats, "agent", "agents")} exceed your subscription limit. Contact support to increase the limit.`;
      }
      return "Agent count limit has been reached. Contact support to change the limit.";
    }
    if (payload.errorObject?.error?.type === "paymentIssue") {
      return "Subscription has not been updated. We had issues processing your payment.";
    }
    return contentMap[ToastContent.DEFAULT_ERROR]();
  },
  [ToastContent.AGENT_UPDATE_SUCCESS]: () => "Agent has been updated.",
  // @ts-expect-error TS2418
  [ToastContent.AGENT_UPDATE_FAILURE]: ({
    lackingSeats,
    paymentIssue,
  }: {
    lackingSeats?: number;
    paymentIssue: boolean;
  }) => {
    if (lackingSeats) {
      return `Agent count limit has been reached. ${lackingSeats} ${plural(lackingSeats, "agent", "agents")} exceed your subscription limit. Contact support to increase the limit.`;
    }
    if (paymentIssue) {
      return "Subscription has not been updated. We had issues processing your payment. Check your payment details or contact us.";
    }
    return "Agent has not been updated.";
  },
  [ToastContent.AGENT_DELETE_SUCCESS]: () => "Agent has been deleted.",
  // @ts-expect-error TS(2339): Property 'isOwnerDeletion' does not exist on type ... Remove this comment to see the full error message
  [ToastContent.AGENT_DELETE_FAILURE]: ({ isOwnerDeletion }) =>
    isOwnerDeletion ? "Organization owner removal is not allowed." : "Agent has not been deleted.",
  [ToastContent.TEAM_CREATE_SUCCESS]: () => "Team has been created.",
  [ToastContent.TEAM_CREATE_FAILURE]: () => "Team has not been created.",
  [ToastContent.TEAM_UPDATE_SUCCESS]: () => "Team has been updated.",
  [ToastContent.TEAM_DELETE_SUCCESS]: () => "Team has been deleted.",
  [ToastContent.TEAM_DELETE_FAILURE]: () => "Team has not been deleted.",
  [ToastContent.TEAM_NOTIFICATIONS_SUCCESS]: () => "Notifications settings have been updated.",
  [ToastContent.TEAM_NOTIFICATIONS_FAILURE]: () => "Notifications settings have not been updated.",
  [ToastContent.COPY_MAILBOX_SUCCESS]: () => "Forwarding address has been copied.",
  [ToastContent.EMAIL_DOMAIN_CREATE_SUCCESS]: () => "Domain has been added.",
  // @ts-expect-error TS2418
  [ToastContent.EMAIL_DOMAIN_CREATE_FAILURE]: (payload: ICreateEmailDomainFailurePayload) => {
    if (payload.errorObject && payload.errorObject.type === "conflict") {
      return "This domain name is already used. Please choose another domain.";
    }
    return "Domain has not been created.";
  },
  [ToastContent.EMAIL_DOMAIN_DELETE_SUCCESS]: () => "Domain has been deleted.",
  [ToastContent.TAG_ADD_SUCCESS]: () => "Tag has been added.",
  // @ts-expect-error TS(2339): Property 'type' does not exist on type 'KeyMap<any... Remove this comment to see the full error message
  [ToastContent.TAG_ADD_FAILURE]: ({ type }) => {
    if (type === "limitExceeded") {
      return "You've reached the limit of added tags. Delete one of your tags to continue.";
    }
    return "Tag has not been added.";
  },
  [ToastContent.TAG_UPDATE_SUCCESS]: () => "Tag has been updated.",
  [ToastContent.TAG_UPDATE_FAILURE]: () => "Tag has not been updated.",
  [ToastContent.TAG_DELETE_SUCCESS]: () => "Tag has been deleted.",
  [ToastContent.TAG_DELETE_FAILURE]: () => "Tag has not been deleted.",
  [ToastContent.CANNED_RESPONSE_ADD_SUCCESS]: () => "Canned response has been added.",
  // @ts-expect-error TS(2339): Property 'type' does not exist on type 'KeyMap<any... Remove this comment to see the full error message
  [ToastContent.CANNED_RESPONSE_ADD_FAILURE]: ({ type }) => {
    if (type === "limitExceeded") {
      return "You've reached the limit of added canned responses. Delete one of your canned responses to continue.";
    }
    return "Canned response has not been added. ";
  },
  [ToastContent.CANNED_RESPONSE_UPDATE_SUCCESS]: () => "Canned response has been updated.",
  [ToastContent.CANNED_RESPONSE_UPDATE_FAILURE]: () => "Canned response has not been updated.",
  [ToastContent.CANNED_RESPONSE_DELETE_SUCCESS]: () => "Canned response has been deleted.",
  [ToastContent.CANNED_RESPONSE_DELETE_FAILURE]: () => "Canned response has not been deleted.",
  [ToastContent.MACRO_CREATE_SUCCESS]: () => "Macro has been added.",
  // @ts-expect-error TS(2339): Property 'type' does not exist on type 'KeyMap<any... Remove this comment to see the full error message
  [ToastContent.MACRO_CREATE_FAILURE]: ({ type, message, maxSharedMacros, maxPrivateMacrosPerAgent }) => {
    if (type === "limitExceeded") {
      if (message.includes("shared")) {
        return `You've reached the limit of ${maxSharedMacros} shared macros. Delete one of your macros to continue.`;
      }
      if (message.includes("private")) {
        return `You've reached the limit of ${maxPrivateMacrosPerAgent} private macros. Delete one of your macros to continue.`;
      }
    }
    return "Macro has not been added.";
  },
  [ToastContent.MACRO_UPDATE_SUCCESS]: () => "Macro has been updated.",
  [ToastContent.MACRO_UPDATE_FAILURE]: () => "Macro has not been updated.",
  [ToastContent.MACRO_DELETE_SUCCESS]: () => "Macro has been deleted.",
  [ToastContent.MACRO_DELETE_FAILURE]: () => "Macro has not been deleted.",
  [ToastContent.MAILBOX_CREATE_SUCCESS]: () => "Forwarding address has been created.",
  // @ts-expect-error TS(2418): Type of computed property's value is '(payload: IE... Remove this comment to see the full error message
  [ToastContent.MAILBOX_CREATE_FAILURE]: (payload: IEntityCreateFailure) => {
    // @ts-expect-error TS(2532): Object is possibly 'undefined'.
    if (payload.errorObject.type === "limitExceeded") {
      // @ts-expect-error TS(2532): Object is possibly 'undefined'.
      return upperFirst(payload.errorObject.message.replace("mailboxes", "forwarding addresses"));
    }
    return "Forwarding address has not been created.";
  },
  [ToastContent.MAILBOX_UPDATE_SUCCESS]: () => "Forwarding address has been updated.",
  [ToastContent.MAILBOX_UPDATE_FAILURE]: () => "Forwarding address has not been updated.",
  [ToastContent.MAILBOX_DELETE_SUCCESS]: () => "Forwarding address has been deleted.",
  [ToastContent.MAILBOX_DELETE_FAILURE]: () => "Forwarding address has not been deleted.",
  [ToastContent.FORWARDING_INSTRUCTIONS_SEND_SUCCESS]: () => "Instructions have been sent.",
  [ToastContent.FORWARDING_INSTRUCTIONS_SEND_FAILURE]: () => "Instructions have not been sent.",
  [ToastContent.REPORT_CREATE_ERROR]: () => "There was a problem with creating this report.",
  [ToastContent.REPORT_CREATE_SUCCESS]: () => "Your report is all scheduled up!",
  [ToastContent.REPORT_DELETE_ERROR]: () => "There was a problem with deleting this report.",
  [ToastContent.REPORT_DELETE_SUCCESS]: () => "Report deleted.",
  [ToastContent.REPORT_EXISTS_ERROR]: () => "Report for this data already exists.",
  [ToastContent.REPORT_SAVE_ERROR]: () => "There was a problem with editing this report.",
  [ToastContent.REPORT_SAVE_SUCCESS]: () => "Report saved.",
  [ToastContent.REPORT_DATE_RANGE_TOO_LONG]: () => "Date range is too long.",
  [ToastContent.REPORT_TOO_MUCH_TEAMS]: () => "Maximum number of teams reached.",
  [ToastContent.REPORT_TOO_MUCH_AGENTS]: () => "Maximum number of agents reached.",
  [ToastContent.REPORT_TOO_MUCH_TAGS]: () => "Maximum number of tags reached.",
  [ToastContent.REPORT_TOO_MUCH_TAGS_IN_TICKET]: () => "Maximum number of tags in ticket reached.",
  [ToastContent.CUSTOM_VIEW_CREATE_SUCCESS]: () => "View has been created.",
  // @ts-expect-error TS(2418): Type of computed property's value is '(payload: IE... Remove this comment to see the full error message
  [ToastContent.CUSTOM_VIEW_CREATE_FAILURE]: (payload: IEntityCreateFailure) => {
    // @ts-expect-error TS(2532): Object is possibly 'undefined'.
    if (payload.errorObject.type === "limitExceeded") {
      // @ts-expect-error TS(2532): Object is possibly 'undefined'.
      return upperFirst(payload.errorObject.message);
    }
    return "View has not been created.";
  },
  [ToastContent.CUSTOM_VIEW_UPDATE_SUCCESS]: () => "View has been updated.",
  [ToastContent.CUSTOM_VIEW_UPDATE_FAILURE]: () => "View has not been updated.",
  [ToastContent.CUSTOM_VIEW_DELETE_SUCCESS]: () => "View has been deleted.",
  [ToastContent.CUSTOM_VIEW_DELETE_FAILURE]: () => "View has not been deleted.",
  [ToastContent.CUSTOM_FIELD_CREATE_SUCCESS]: () => "Custom field has been created.",
  [ToastContent.CUSTOM_FIELD_CREATE_FAILURE]: () => "Custom field has not been created.",
  [ToastContent.CUSTOM_FIELD_ACTIVATED_SUCCESS]: () => "Custom field has been activated.",
  [ToastContent.CUSTOM_FIELD_DEACTIVATED_SUCCESS]: () => "Custom field has been deactivated.",
  [ToastContent.CUSTOM_FIELD_UPDATE_SUCCESS]: () => "Custom field has been updated.",
  [ToastContent.CUSTOM_FIELD_UPDATE_FAILURE]: () => "Custom field has not been updated.",
  [ToastContent.CUSTOM_FIELD_DELETE_SUCCESS]: () => "Custom field has been deleted.",
  [ToastContent.CUSTOM_FIELD_DELETE_FAILURE]: () => "Custom field has not been deleted.",
  [ToastContent.FORWARDING_ADDRESS_COPIED]: () => "Forwarding address has been copied.",
  [ToastContent.CONTACT_FORM_CODE_COPIED]: () => "Contact form code has been copied.",
  // @ts-expect-error TS(2339): Property 'recordType' does not exist on type 'KeyM... Remove this comment to see the full error message
  [ToastContent.DOMAIN_CONFIGURATION_COPIED]: ({ recordType, field }) =>
    `${recordType} record ${field} has been copied.`,
  [ToastContent.TRANSACTION_ATTACHMENTS_LIMIT_REACHED]: () =>
    "Attachment not added. Entire message can't exceed 10 MB limit.",
  // @ts-expect-error TS(2339): Property 'accept' does not exist on type 'KeyMap<a... Remove this comment to see the full error message
  [ToastContent.TRANSACTION_ATTACHMENT_NOT_IN_VALID_FORMAT]: ({ accept }) => {
    const imageAcceptList = ["image/jpeg", "image/png", "image/gif", "image/webp", "image/bmp"];

    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (accept && accept.every((type: any, index: any) => type === imageAcceptList[index])) {
      return "Invalid image format. Only JPG, PNG, GIF, WEBP and BMP images are valid.";
    }

    return "Failed to add attachment - invalid format.";
  },
  [ToastContent.TRANSACTION_NOT_FOUND]: () => "Session expired. Reupload your attachments and images.",
  // @ts-expect-error TS(2339): Property 'minutesElapsed' does not exist on type '... Remove this comment to see the full error message
  [ToastContent.TICKET_FETCH_ORIGINAL_MESSAGE_FAILED]: ({ minutesElapsed }) =>
    minutesElapsed > 3600
      ? "Original message no longer available."
      : "Original message not available yet. Try again later.",
  [ToastContent.TICKET_FETCH_UNSTRIPPED_MESSAGE_FAILED]: () => "Could not fetch cut content.",
  [ToastContent.UPDATE_BILLING_ADDRESS_FAILED]: () => "Billing address has not been updated.",
  [ToastContent.UPDATE_BILLING_ADDRESS_SUCCESS]: () => "Billing address has been updated.",
  // @ts-expect-error TS(2339): Property 'duplicated' does not exist on type 'KeyM... Remove this comment to see the full error message
  [ToastContent.RULE_CREATE_SUCCESS]: ({ duplicated }) => `Rule has been ${duplicated ? "duplicated" : "created"}.`,
  [ToastContent.RULE_UPDATE_SUCCESS]: () => "Rule has been updated.",
  [ToastContent.RULE_ACTIVE_SUCCESS]: () => "Rule has been turned on.",
  [ToastContent.RULE_INACTIVE_SUCCESS]: () => "Rule has been turned off.",
  // @ts-expect-error TS(2339): Property 'type' does not exist on type 'KeyMap<any... Remove this comment to see the full error message
  [ToastContent.RULE_UPDATE_FAILURE]: ({ type }) => {
    if (type === "planLimitExceeded") {
      return isInIframe()
        ? "Plan limit exceeded. Please contact support to increase your limits."
        : "You've reached the limit of added rules. Delete one of your rules to continue.";
    }

    return "Rule has not been updated.";
  },
  // @ts-expect-error TS(2339): Property 'duplicated' does not exist on type 'KeyM... Remove this comment to see the full error message
  [ToastContent.RULE_CREATE_FAILURE]: ({ duplicated, type }) => {
    if (type === "planLimitExceeded") {
      return isInIframe()
        ? "Plan limit exceeded. Please contact support to increase your limits."
        : "You've reached the limit of added rules. Delete one of your rules to continue.";
    }
    return `Rule has not been ${duplicated ? "duplicated" : "created"}.`;
  },

  [ToastContent.RULE_DELETE_SUCCESS]: () => "Rule has been deleted.",
  [ToastContent.RULE_DELETE_FAILURE]: () => "Rule has not been deleted.",
  [ToastContent.EMAIL_TEMPLATE_TEST_SUCCESS]: () => "Email template test has been sent.",
  // @ts-expect-error TS(2339): Property 'type' does not exist on type 'KeyMap<any... Remove this comment to see the full error message
  [ToastContent.EMAIL_TEMPLATE_TEST_FAILURE]: ({ type, message: error }) => {
    if (type === "limitExceeded") {
      if (error.includes("hard bounces") || error.includes("spam notifications")) {
        return "Temporary lock. Too many email hard-bounces generated in the last 24h.";
      }
      if (error.includes("spam complaints")) {
        return "Temporary lock. Too many spam complaints generated in the last 24h.";
      }
      if (error.includes("verification")) {
        return "Message limit for unverified license exceeded. Please verify the account.";
      }
      if (error.includes("unsubscriptions")) {
        return "Temporary lock. Too many opt-outs generated in the last 24h.";
      }
      return "Message limit for trial license exceeded. Limit will be lifted automatically.";
    }

    return "Email template test has not been sent.";
  },
  [ToastContent.EMAIL_TEMPLATE_CREATE_SUCCESS]: () => "Email template has been created.",
  // @ts-expect-error TS(2339): Property 'type' does not exist on type 'KeyMap<any... Remove this comment to see the full error message
  [ToastContent.EMAIL_TEMPLATE_CREATE_FAILURE]: ({ type }) => {
    if (isInIframe() && type === "planLimitExceeded") {
      return "Plan limit exceeded. Please contact support to increase your limits.";
    }
    return "Email template has not been created.";
  },
  [ToastContent.EMAIL_TEMPLATE_UPDATE_SUCCESS]: () => "Email template has been updated.",
  [ToastContent.EMAIL_TEMPLATE_UPDATE_FAILURE]: () => "Email template has not been updated.",
  [ToastContent.EMAIL_TEMPLATE_DELETE_SUCCESS]: () => "Email template has been deleted.",
  [ToastContent.EMAIL_TEMPLATE_DELETE_FAILURE]: () => "Email template has not been deleted.",
  [ToastContent.GENERATE_REPORT_SUCCESS]: () => "Report will land in the mailboxes you specified in a few minutes.",
  // @ts-expect-error TS(2339): Property 'reason' does not exist on type 'KeyMap<a... Remove this comment to see the full error message
  [ToastContent.GENERATE_REPORT_FAILURE]: ({ reason, limitReason: error }) => {
    switch (reason) {
      case "reportInProgress":
        return "Report is already on the way.";
      case "invalidRange":
        return "Date range exceeds 3 months. Reduce the range.";
      case "limitExceeded": {
        if (error.includes("hard bounces") || error.includes("spam notifications")) {
          return "Temporary lock. Too many email hard-bounces generated in the last 24h.";
        }
        if (error.includes("spam complaints")) {
          return "Temporary lock. Too many spam complaints generated in the last 24h.";
        }
        if (error.includes("verification")) {
          return "Message limit for unverified license exceeded. Please verify the account.";
        }
        if (error.includes("unsubscriptions")) {
          return "Temporary lock. Too many opt-outs generated in the last 24h.";
        }
        return "Message limit for trial license exceeded. Limit will be lifted automatically.";
      }
      default:
        return "Failed to generate report.";
    }
  },
  [ToastContent.SCREEN_RECORDING_UPLOAD_IN_PROGRESS]: () => "Screen recording upload in progress. Please wait…",
  [ToastContent.SCREEN_RECORDING_FAILURE]: () => "Screen recording has not been created.",
  // @ts-expect-error TS(2339): Property 'transitioned' does not exist on type 'Ke... Remove this comment to see the full error message
  [ToastContent.SCREEN_RECORDING_UPLOAD_SUCCESS]: ({ transitioned, showUrl }) => {
    if (showUrl) {
      return (
        <>
          Screencast has been saved: <ExternalLink href={showUrl}>Show</ExternalLink>
        </>
      );
    }

    return transitioned
      ? "Screen recording link has been added to your clipboard."
      : "Screen recording link has been added to message.";
  },
  // @ts-expect-error TS(2339): Property 'tagReminder' does not exist on type 'Key... Remove this comment to see the full error message
  [ToastContent.TAG_REMINDER_SUCCESS]: ({ tagReminder }) =>
    tagReminder ? "Tag reminder has been activated." : "Tag reminder has been deactivated.",
  [ToastContent.TAG_REMINDER_FAILURE]: () => "Tag reminder settings has not been updated.",
  // @ts-expect-error TS(2339): Property 'type' does not exist on type 'KeyMap<any... Remove this comment to see the full error message
  [ToastContent.REPLY_ADDRESS_CREATE_FAILURE]: ({ type }) => {
    if (type === "limitExceeded") {
      return "You've reached the limit of added reply addresses. Delete one of your addresses to continue.";
    }
    return "Reply address has not been created.";
  },
  [ToastContent.REPLY_ADDRESS_CREATE_SUCCESS]: () => "Reply address has been created.",
  [ToastContent.REPLY_ADDRESS_UPDATE_SUCCESS]: () => "Reply address has been updated.",
  [ToastContent.REPLY_ADDRESS_UPDATE_FAILURE]: () => "Reply address has not been updated.",
  [ToastContent.REPLY_ADDRESS_DELETE_SUCCESS]: () => "Reply address has been deleted.",
  [ToastContent.REPLY_ADDRESS_DELETE_FAILURE]: () => "Reply address has not been deleted.",
  [ToastContent.DELETE_FOLLOWER_SUCCESS]: () => "Follower has been deleted.",
  [ToastContent.DELETE_FOLLOWER_FAILURE]: () => "Follower has not been deleted.",
  [ToastContent.ADD_FOLLOWER_SUCCESS]: () => "Follower has been added.",
  // @ts-expect-error TS(2339): Property 'type' does not exist on type 'KeyMap<any... Remove this comment to see the full error message
  [ToastContent.ADD_FOLLOWER_FAILURE]: ({ type }) =>
    type === "limitExceeded" ? "Followers limit for ticket exceeded." : "Follower has not been added.",
  // @ts-expect-error TS(2339): Property 'progressChannel' does not exist on type ... Remove this comment to see the full error message
  [ToastContent.BULK_ACTION_PROGRESS]: ({ progressChannel, count }) => (
    <>
      <p>Updating tickets…</p>
      <BulkActionProgressBar progressChannel={progressChannel} count={count} />
    </>
  ),
  // @ts-expect-error TS(2339): Property 'count' does not exist on type 'KeyMap<an... Remove this comment to see the full error message
  [ToastContent.BULK_ACTION_SUCCESS]: ({ count, silo }) => {
    switch (silo) {
      case TicketSilo.Archive:
        return `${count} ${plural(count, "ticket has", "tickets have")} been moved to Archive.`;
      case TicketSilo.Spam:
        return `${count} ${plural(count, "ticket has", "tickets have")} been moved to Spam.`;
      case TicketSilo.Trash:
        return `${count} ${plural(count, "ticket has", "tickets have")} been moved to Trash.`;
      case TicketSilo.Tickets:
        return `${count} ${plural(count, "ticket has", "tickets have")} been moved to all recent tickets.`;
      default:
        return `${count} ${plural(count, "ticket has", "tickets have")} been updated.`;
    }
  },
  // @ts-expect-error TS(2339): Property 'count' does not exist on type 'KeyMap<an... Remove this comment to see the full error message
  [ToastContent.BULK_ACTION_FAILURE]: ({ count }) =>
    `${count} ${plural(count, "ticket", "tickets")} has not been updated.`,
  // @ts-expect-error TS(2339): Property 'progressChannel' does not exist on type ... Remove this comment to see the full error message
  [ToastContent.BULK_DELETE_PROGRESS]: ({ progressChannel, count }) => (
    <>
      <p>Deleting tickets…</p>
      <BulkActionProgressBar progressChannel={progressChannel} count={count} />
    </>
  ),
  // @ts-expect-error TS(2339): Property 'count' does not exist on type 'KeyMap<an... Remove this comment to see the full error message
  [ToastContent.BULK_DELETE_SUCCESS]: ({ count }) => `${count} ${plural(count, "ticket", "tickets")} has been deleted.`,
  // @ts-expect-error TS(2339): Property 'count' does not exist on type 'KeyMap<an... Remove this comment to see the full error message
  [ToastContent.BULK_DELETE_FAILURE]: ({ count }) =>
    `${count} ${plural(count, "ticket", "tickets")} has not been deleted.`,
  // @ts-expect-error TS(2339): Property 'email' does not exist on type 'KeyMap<an... Remove this comment to see the full error message
  [ToastContent.RESEND_VERIFICATION_SUCCESS]: ({ email }) => `The verification link has been sent to ${email}`,
  [ToastContent.RESEND_INVITATION_SUCCESS]: () => "Reminder has been sent.",
  [ToastContent.RESEND_INVITATION_FAILURE]: () => "Reminder has not been sent.",
  // @ts-expect-error TS(2339): Property 'type' does not exist on type 'KeyMap<any... Remove this comment to see the full error message
  [ToastContent.ADD_BLOCKED_ENTRY_FAILURE]: ({ type }) => {
    if (type === "limitExceeded") {
      return "You've reached the limit of added blocked addresses. Delete one of your addresses to continue.";
    }
    if (type === "conflict") {
      return "Address has already been added to blocked list.";
    }
    return "Address was not added to blocked list because it is invalid.";
  },
  // @ts-expect-error TS(2339): Property 'type' does not exist on type 'KeyMap<any... Remove this comment to see the full error message
  [ToastContent.ADD_TRUSTED_ENTRY_FAILURE]: ({ type }) => {
    if (type === "limitExceeded") {
      return "You've reached the limit of added trusted addresses. Delete one of your addresses to continue.";
    }
    if (type === "conflict") {
      return "Address has already been added to trusted list.";
    }
    return "Address was not added to trusted list because it is invalid.";
  },
  [ToastContent.ADD_TRUSTED_ENTRY_SUCCESS]: () => "Address has been added to trusted list.",
  [ToastContent.ADD_BLOCKED_ENTRY_SUCCESS]: () => "Address has been added to blocked list.",
  [ToastContent.REMOVE_BLOCKED_ENTRY_FAILURE]: () => "Address has not been removed from blocked list.",
  [ToastContent.REMOVE_TRUSTED_ENTRY_FAILURE]: () => "Address has not been removed from trusted list.",
  [ToastContent.REMOVE_BLOCKED_ENTRY_SUCCESS]: () => "Address has been removed from blocked list.",
  [ToastContent.REMOVE_TRUSTED_ENTRY_SUCCESS]: () => "Address has been removed from trusted list.",
  // @ts-expect-error TS(2339): Property 'progressChannel' does not exist on type ... Remove this comment to see the full error message
  [ToastContent.QUEUED_BLOCKED_ENTRIES_PROGRESS]: ({ progressChannel, count }) => (
    <>
      <p>Adding addresses to blocked list…</p>
      <BulkActionProgressBar progressChannel={progressChannel} count={count} />
    </>
  ),
  // @ts-expect-error TS(2339): Property 'progressChannel' does not exist on type ... Remove this comment to see the full error message
  [ToastContent.QUEUED_TRUSTED_ENTRIES_PROGRESS]: ({ progressChannel, count }) => (
    <>
      <p>Adding addresses to trusted list…</p>
      <BulkActionProgressBar progressChannel={progressChannel} count={count} />
    </>
  ),
  // @ts-expect-error TS(2339): Property 'successCount' does not exist on type 'Ke... Remove this comment to see the full error message
  [ToastContent.QUEUED_TRUSTED_ENTRIES_PROGRESS_DONE]: ({ successCount, failureCount }) =>
    `${successCount} ${plural(successCount, "address", "addresses")} added to trusted list.${
      failureCount > 0 ? ` Failed to add ${failureCount} ${plural(failureCount, "address", "addresses")}.` : ""
    }`,
  // @ts-expect-error TS(2339): Property 'successCount' does not exist on type 'Ke... Remove this comment to see the full error message
  [ToastContent.QUEUED_BLOCKED_ENTRIES_PROGRESS_DONE]: ({ successCount, failureCount }) =>
    `${successCount} ${plural(successCount, "address", "addresses")} added to blocked list.${
      failureCount > 0 ? ` Failed to add ${failureCount} ${plural(failureCount, "address", "addresses")}.` : ""
    }`,
  [ToastContent.CREATE_CONTACT_FORM_SUCCESS]: () => "Contact form has been created.",
  // @ts-expect-error TS(2339): Property 'type' does not exist on type 'KeyMap<any... Remove this comment to see the full error message
  [ToastContent.CREATE_CONTACT_FORM_FAILURE]: ({ type }) =>
    type === "limitExceeded" ? "Number of contact forms exceeded." : "Contact form has not been created.",
  [ToastContent.UPDATE_CONTACT_FORM_SUCCESS]: () => "Contact form has been updated.",
  [ToastContent.UPDATE_CONTACT_FORM_FAILURE]: () => "Contact form has not been updated.",
  [ToastContent.DELETE_CONTACT_FORM_SUCCESS]: () => "Contact form has been deleted.",
  [ToastContent.DELETE_CONTACT_FORM_FAILURE]: () => "Contact form has not been deleted.",
  // @ts-expect-error TS(2339): Property 'progressChannel' does not exist on type ... Remove this comment to see the full error message
  [ToastContent.MERGE_TICKETS_PROGRESS]: ({ progressChannel, count }) => (
    <>
      <p>Merging tickets…</p>
      <BulkActionProgressBar progressChannel={progressChannel} count={count} />
    </>
  ),
  [ToastContent.MERGE_TICKETS_SUCCESS]: () => "Merging tickets success.",
  [ToastContent.MERGE_TICKETS_FAILURE]: () => "Merging tickets failure.",
  [ToastContent.UPDATE_DEFAULT_TEMPLATE_SUCCESS]: () => "Default template has been updated.",
  [ToastContent.UPDATE_DEFAULT_TEMPLATE_FAILURE]: () => "Default template has not been updated.",
  [ToastContent.AI_TEXT_ADUST_ENHANCEMENT_SUCCESS]: () => "Text has been adjusted.",
  [ToastContent.AI_TEXT_ADUST_ENHANCEMENT_FAILURE]: () => "Text has not been adjusted.",
  [ToastContent.AI_TEXT_EXPAND_ENHANCEMENT_SUCCESS]: () => "Text has been expanded.",
  [ToastContent.AI_TEXT_EXPAND_ENHANCEMENT_FAILURE]: () => "Text has not been expanded.",
  [ToastContent.AI_TEXT_CORRECT_ENHANCEMENT_SUCCESS]: () => "Text has been corrected.",
  [ToastContent.AI_TEXT_CORRECT_ENHANCEMENT_FAILURE]: () => "Text has not been corrected.",
  [ToastContent.APPLICATION_INSTALL_ERROR]: () => "Could not enable integration. Please inform us.",
  [ToastContent.APPLICATION_UNINSTALL_SUCCESS]: () => "The application has been successfully deleted.",
  [ToastContent.APPLICATION_UNINSTALL_ERROR]: () => "Could not disable integration. Please inform us.",
  [ToastContent.ATTACHMENT_DELETE_FAILURE]: () => "Attachment has not been deleted.",
  [ToastContent.ATTACHMENT_DELETE_SUCCESS]: () => "Attachment has been deleted.",
  [ToastContent.FETCH_NEXT_TICKET_FAILED]: () => "End of the ticket list has been reached.",
  [ToastContent.FETCH_PREV_TICKET_FAILED]: () => "Top of the ticket list has been reached.",
  [ToastContent.LOGO_UPLOAD_SUCCESS]: () => "File has been uploaded.",
  // @ts-expect-error TS(2339): Property 'type' does not exist on type 'KeyMap<any... Remove this comment to see the full error message
  [ToastContent.LOGO_UPLOAD_FAILURE]: ({ type }) => {
    switch (type) {
      case FileUploadErrorType.InvalidSize: {
        return "File upload has failed. Max. file size is 1MB. Try to upload another file.";
      }
      case FileUploadErrorType.InvalidExtension: {
        return "File upload has failed. Supported file formats are JPG, JPEG, PNG. Try to upload another file.";
      }
      default: {
        return "File upload has failed.";
      }
    }
  },
  [ToastContent.OPENWIDGET_CONNECT_SUCCESS]: () => "OpenWidget has been connected.",
  [ToastContent.OPENWIDGET_CONNECT_FAILURE]: () => "OpenWidget has not been connected.",
  [ToastContent.OPENWIDGET_DISCONNECT_SUCCESS]: () => "OpenWidget has been disconnected.",
  [ToastContent.OPENWIDGET_DISCONNECT_FAILURE]: () => "OpenWidget has not been disconnected.",
  [ToastContent.OPENWIDGET_UPDATE_SUCCESS]: () => "OpenWidget team has been updated.",
  [ToastContent.OPENWIDGET_UPDATE_FAILURE]: () => "OpenWidget team has not been updated.",
  [ToastContent.OPENWIDGET_COPY_CODE_SUCCESS]: () => "OpenWidget code has been copied.",
  [ToastContent.OPENWIDGET_COPY_CODE_FAILURE]: () => "OpenWidget code has not been copied.",
  // @ts-expect-error TS(2339): Property 'limit' does not exist on type 'KeyMap<an... Remove this comment to see the full error message
  [ToastContent.AGENTS_LIMIT_IN_FILTERS_REACHED]: ({ limit }) =>
    `You've reached the limit of ${limit} agents in filters.`,
  // @ts-expect-error TS(2339): Property 'limit' does not exist on type 'KeyMap<an... Remove this comment to see the full error message
  [ToastContent.LANGUAGES_LIMIT_IN_FILTERS_REACHED]: ({ limit }) =>
    `You've reached the limit of ${limit} languages in filters.`,
  [ToastContent.AUTO_ASSIGNMENT_CHANGED_SUCCESS]: () => "Auto-assignment settings updated.",
  [ToastContent.AUTO_ASSIGNMENT_CHANGED_FAILURE]: () => "Auto-assignment settings has not been updated",
};

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getToastContent = (contentType: ToastContent, templateVars?: KeyMap<any>): React.ReactNode =>
  contentMap[contentType] ? contentMap[contentType](templateVars) : "";
